/**
 * Modal de clasificacion multi-concepto — Sprint 12 S12-04
 * (migrado al portal en Sprint 13 S13-04).
 *
 * Consume POST /pagos/{id}/clasificar de S11-V2-10 con desglose
 * multi-linea (cuota + multa + otro_ingreso). Pre-carga datos de
 * auto-distribucion si existe propuesta (GET sugerencia-distribucion).
 *
 * Tipos de linea soportados en S12-04 MVP:
 * - `cuota`: requiere `cuota_id`. Aplica monto a una cuota pendiente
 *   del lote elegido.
 * - `multa`: requiere `categoria_id` (tipo ingreso) + descripcion +
 *   monto. Crea un IngresoVario contable.
 * - `otro_ingreso`: requiere `categoria_id` (tipo ingreso) +
 *   descripcion + monto. Crea un IngresoVario contable.
 *
 * Tipos no soportados en S12-04 (entran en sprints futuros si emerge
 * necesidad): venta_recurso, aporte_proyecto, saldo_a_favor.
 *
 * UX clave:
 * - Suma de montos en vivo vs `pago.monto_total`. Submit deshabilitado
 *   si no cuadra.
 * - Cambiar el lote_id resetea `cuota_id` de las lineas tipo cuota
 *   (las cuotas pertenecen al lote anterior).
 * - Errores del backend:
 *   - 404: pago no encontrado (no deberia pasar; cierra modal).
 *   - 409: pago ya clasificado (otro tesorero?). Cierra modal +
 *     refresca listado.
 *   - 422: validacion semantica (suma no cuadra, categoria no apta,
 *     etc). Muestra detail del backend.
 *   - Otros: mensaje generico.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { clasificarPago, getSugerenciaDistribucion } from '../../lib/api/pagos';
import { listLotes } from '../../lib/api/lotes';
import { listCuotas } from '../../lib/api/cuotas';
import { listCategorias } from '../../lib/api/categorias';
import { CategoriaSelector } from '../../components/tesoreria/CategoriaSelector';
import {
  listIngresosVarios,
  type IngresoVarioRead,
} from '../../lib/api/ingresos-varios';
import type {
  CategoriaRead,
  CuotaRead,
  LineaDistribucionInput,
  LoteResumen,
  PagoRead,
  SugerenciaDistribucionPropuesta,
  TipoDistribucion,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { formatCLP, formatFecha } from '../../lib/format';
import { Monto, Num } from '../../components/tesoreria/Monto';

interface LineaLocal extends LineaDistribucionInput {
  id_local: string;
}

interface ModalClasificarProps {
  pago: PagoRead;
  onCerrar: () => void;
  onClasificado: () => void;
}

// Tipos visibles en el selector. aporte_proyecto entra si emerge
// necesidad. venta_recurso agregado en S15-Y para clasificar pagos de
// control remoto / llavero / etc contra el Recurso correspondiente.
// saldo_a_favor agregado al volverse la auto-distribucion FIFO (ensayo
// LIVE 2026-06): el sobrante de un pago tras abonar todas las cuotas
// del ano se modela como linea saldo_a_favor (sin FK), y la sugerencia
// puede pre-cargarla — debe existir como opcion para que el tesorero
// no la pierda al editar.
const TIPOS_LINEA: { value: TipoDistribucion; label: string }[] = [
  { value: 'cuota', label: 'Cuota' },
  { value: 'multa', label: 'Multa' },
  { value: 'otro_ingreso', label: 'Otro ingreso' },
  { value: 'venta_recurso', label: 'Venta de recurso' },
  { value: 'saldo_a_favor', label: 'Saldo a favor' },
];

function lineaVacia(tipo: TipoDistribucion = 'cuota', monto = 0): LineaLocal {
  return {
    id_local: `line-${Math.random().toString(36).slice(2, 10)}`,
    tipo,
    descripcion: '',
    monto_aplicado: monto,
    cuota_id: null,
    recurso_id: null,
    proyecto_id: null,
    categoria_id: null,
    ingreso_vario_id: null,
  };
}

export function ModalClasificar({
  pago,
  onCerrar,
  onClasificado,
}: ModalClasificarProps) {
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [cuotas, setCuotas] = useState<CuotaRead[]>([]);
  // Ventas pendientes de cobro (IngresoVario `por_cobrar`) de la casa: es lo
  // que una línea "Venta de recurso" debe cobrar (solicitud del vecino /
  // asignación directa). Antes se ofrecían recursos ya `entregado`, que es
  // justo lo que NO hay que cobrar.
  const [ventasPendientes, setVentasPendientes] = useState<IngresoVarioRead[]>(
    [],
  );
  const [categoriasIngreso, setCategoriasIngreso] = useState<CategoriaRead[]>([]);
  const [sugerencia, setSugerencia] =
    useState<SugerenciaDistribucionPropuesta | null>(null);
  const [motivoSugerencia, setMotivoSugerencia] = useState<string | null>(null);
  const [loteId, setLoteId] = useState<string>(pago.lote_id ?? '');
  const [lineas, setLineas] = useState<LineaLocal[]>([lineaVacia('cuota', pago.monto_total)]);
  const [aprenderAlias, setAprenderAlias] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Al mount: cargar selectores + sugerencia en paralelo.
  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    Promise.all([
      listLotes({ activo: true }),
      listCategorias({ tipo: 'ingreso', activa: true }),
      getSugerenciaDistribucion(pago.id).catch(() => ({
        sugerencia: null,
        motivo: null,
      })),
    ])
      .then(([lotesData, catsData, sugData]) => {
        if (cancelado) return;
        setLotes(lotesData);
        setCategoriasIngreso(catsData);
        setSugerencia(sugData.sugerencia);
        setMotivoSugerencia(sugData.motivo);
        // Si hay sugerencia auto, pre-cargar lote y las N lineas FIFO
        // (una por cuota abonada + opcional saldo_a_favor del sobrante).
        if (sugData.sugerencia) {
          setLoteId(sugData.sugerencia.lote_id);
          setLineas(
            sugData.sugerencia.lineas.map((item) => ({
              ...lineaVacia(
                item.tipo as TipoDistribucion,
                item.monto,
              ),
              descripcion:
                item.tipo === 'saldo_a_favor'
                  ? 'Saldo a favor (sugerencia auto)'
                  : 'Cuota (sugerencia auto)',
              cuota_id: item.cuota_id,
            })),
          );
        }
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar datos del modal`
            : 'Error de red al cargar datos del modal',
        );
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [pago.id, pago.monto_total]);

  // Cuando cambia el lote_id, cargar cuotas con saldo pendiente
  // (pendiente, en_abono, vencida) y las ventas pendientes de cobro
  // (IngresoVario `por_cobrar`). `cambiarLote` resetea cuota_id, recurso_id e
  // ingreso_vario_id en las lineas que apuntaban al lote anterior. Las cuotas
  // en_abono pueden tener saldo > 0 todavia y deben aparecer como destino.
  useEffect(() => {
    if (!loteId) {
      setCuotas([]);
      setVentasPendientes([]);
      return;
    }
    let cancelado = false;
    Promise.all([
      listCuotas({ lote_id: loteId }).catch(() => [] as CuotaRead[]),
      // Ventas del lote pendientes de cobro (IngresoVario `por_cobrar`): la
      // solicitud de recurso aprobada crea una venta `por_cobrar`; el recurso
      // físico se asigna desde stock recién tras el cobro.
      listIngresosVarios({ lote_id: loteId, estado: 'por_cobrar' }).catch(
        () => [] as IngresoVarioRead[],
      ),
    ]).then(([cuotasData, ventasData]) => {
      if (cancelado) return;
      const conSaldo = cuotasData.filter((c) =>
        ['pendiente', 'en_abono', 'vencida'].includes(c.estado),
      );
      setCuotas(conSaldo);
      setVentasPendientes(ventasData);
    });
    return () => {
      cancelado = true;
    };
  }, [loteId]);

  // Suma calculada en vivo.
  const sumaActual = useMemo(
    () =>
      lineas.reduce((acc, l) => acc + (Number.isFinite(l.monto_aplicado) ? l.monto_aplicado : 0), 0),
    [lineas],
  );
  const sumaCuadra = sumaActual === pago.monto_total;

  function actualizarLinea(idLocal: string, parche: Partial<LineaLocal>) {
    setLineas((prev) =>
      prev.map((l) => (l.id_local === idLocal ? { ...l, ...parche } : l)),
    );
  }

  function agregarLinea() {
    setLineas((prev) => [...prev, lineaVacia('cuota', 0)]);
  }

  function eliminarLinea(idLocal: string) {
    setLineas((prev) => prev.filter((l) => l.id_local !== idLocal));
  }

  function cambiarLote(nuevoLoteId: string) {
    setLoteId(nuevoLoteId);
    // Reset cuota_id / recurso_id en lineas que apuntaban al lote viejo.
    setLineas((prev) =>
      prev.map((l) => {
        if (l.tipo === 'cuota') return { ...l, cuota_id: null };
        if (l.tipo === 'venta_recurso')
          return { ...l, recurso_id: null, ingreso_vario_id: null };
        return l;
      }),
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!loteId) {
      setError('Falta seleccionar la casa.');
      return;
    }
    if (!sumaCuadra) {
      setError(
        `La suma de las líneas (${formatCLP(sumaActual)}) debe igualar el monto del pago (${formatCLP(pago.monto_total)}).`,
      );
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const lineasParaBackend: LineaDistribucionInput[] = lineas.map(
        ({ id_local: _ignore, ...rest }) => rest,
      );
      await clasificarPago(pago.id, {
        lote_id: loteId,
        lineas: lineasParaBackend,
        aprender_alias: aprenderAlias,
      });
      onClasificado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        if (err.status === 409) {
          setError(`Pago ya clasificado (otro operador?). ${detail}`);
        } else if (err.status === 422 || err.status === 400) {
          setError(detail);
        } else if (err.status === 404) {
          setError('El pago no existe. Cierra el modal y refresca el listado.');
        } else {
          setError(detail);
        }
      } else {
        setError('Error de red al clasificar.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-clasificar-titulo"
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2
              id="modal-clasificar-titulo"
              className="text-xl font-medium text-text-primary"
            >
              Clasificar pago
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              <Monto value={pago.monto_total} /> · {pago.remitente_nombre} ·{' '}
              {pago.email_template_detectado} ·{' '}
              {formatFecha(pago.fecha_transferencia)}
            </p>
            {pago.mensaje_email && pago.mensaje_email.trim() !== '' && (
              <p className="text-xs text-text-muted mt-1">
                Mensaje del banco: "{pago.mensaje_email}"
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="text-text-secondary hover:text-text-primary text-xl leading-none"
          >
            ×
          </button>
        </div>

        {cargando && (
          <p className="text-sm text-text-muted py-4">Cargando datos…</p>
        )}

        {!cargando && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {sugerencia && (
              <div className="bg-state-success/15 border-l-4 border-state-success rounded-sm p-3 text-sm text-text-primary">
                <strong className="font-medium">Sugerencia automática:</strong>{' '}
                distribuir <Monto value={sugerencia.monto} /> FIFO en{' '}
                {sugerencia.lineas.length} línea
                {sugerencia.lineas.length === 1 ? '' : 's'} (cuota más antigua
                primero), ruta <em>{sugerencia.ruta}</em>. Datos pre-cargados
                abajo; ajusta si es necesario.
              </div>
            )}
            {!sugerencia && motivoSugerencia && (
              <div className="bg-bg-sunken border-l-4 border-text-muted rounded-sm p-3 text-xs text-text-secondary">
                Sin sugerencia automática. Motivo:{' '}
                <Num>{motivoSugerencia}</Num>
              </div>
            )}

            <div>
              <label
                htmlFor="modal-lote-select"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                Casa
              </label>
              <select
                id="modal-lote-select"
                required
                value={loteId}
                onChange={(e) => cambiarLote(e.target.value)}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
              >
                <option value="">— Selecciona la casa —</option>
                {lotes.map((l) => {
                  const nombres = l.responsables_pago_nombres.join(' / ');
                  const label = nombres
                    ? `${l.identificador} — ${nombres} — ${l.sector}`
                    : `${l.identificador} — ${l.direccion} (${l.sector})`;
                  return (
                    <option key={l.id} value={l.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-text-primary">
                  Desglose ({lineas.length} línea{lineas.length === 1 ? '' : 's'})
                </label>
                <button
                  type="button"
                  onClick={agregarLinea}
                  className="text-sm text-orange hover:text-orange-light"
                >
                  + Agregar línea
                </button>
              </div>

              {lineas.map((linea, idx) => (
                <FilaLinea
                  key={linea.id_local}
                  indice={idx}
                  linea={linea}
                  cuotas={cuotas}
                  ventasPendientes={ventasPendientes}
                  categoriasIngreso={categoriasIngreso}
                  onChange={(parche) => actualizarLinea(linea.id_local, parche)}
                  onEliminar={
                    lineas.length > 1
                      ? () => eliminarLinea(linea.id_local)
                      : undefined
                  }
                />
              ))}
            </div>

            <div
              className={`p-3 rounded-sm text-sm font-medium ${
                sumaCuadra
                  ? 'bg-state-success/15 text-state-success'
                  : 'bg-state-warning/15 text-state-warning'
              }`}
              data-testid="suma-status"
            >
              Suma del desglose: <Monto value={sumaActual} /> / Pago:{' '}
              <Monto value={pago.monto_total} />
              {!sumaCuadra && (
                <span className="ml-2">
                  (diferencia: <Monto value={Math.abs(sumaActual - pago.monto_total)} />)
                </span>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={aprenderAlias}
                onChange={(e) => setAprenderAlias(e.target.checked)}
                className="rounded-sm accent-orange"
              />
              <span>
                Aprender alias del remitente "{pago.remitente_nombre}" para
                futuros pagos de la misma persona.
              </span>
            </label>

            {error && (
              <div
                className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-2">
              <Button type="button" variant="ghost" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={enviando || !sumaCuadra || !loteId}
              >
                {enviando ? 'Clasificando…' : 'Clasificar pago'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

interface FilaLineaProps {
  indice: number;
  linea: LineaLocal;
  cuotas: CuotaRead[];
  ventasPendientes: IngresoVarioRead[];
  categoriasIngreso: CategoriaRead[];
  onChange: (parche: Partial<LineaLocal>) => void;
  onEliminar?: () => void;
}

function FilaLinea({
  indice,
  linea,
  cuotas,
  ventasPendientes,
  categoriasIngreso,
  onChange,
  onEliminar,
}: FilaLineaProps) {
  const requiereCuota = linea.tipo === 'cuota';
  const requiereRecurso = linea.tipo === 'venta_recurso';
  const requiereCategoria = linea.tipo === 'multa' || linea.tipo === 'otro_ingreso';

  return (
    <div
      className="bg-bg-sunken border-[0.5px] border-white/[0.08] rounded-sm p-3 space-y-2"
      data-testid={`linea-${indice}`}
    >
      <div className="flex gap-2 items-center">
        <select
          aria-label={`Tipo línea ${indice + 1}`}
          value={linea.tipo}
          onChange={(e) =>
            onChange({
              tipo: e.target.value as TipoDistribucion,
              cuota_id: null,
              recurso_id: null,
              categoria_id: null,
              ingreso_vario_id: null,
            })
          }
          className="rounded-sm bg-bg-card border-[0.5px] border-white/15 text-text-primary px-2 py-1 text-sm"
        >
          {TIPOS_LINEA.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          aria-label={`Descripción línea ${indice + 1}`}
          placeholder="Descripción"
          value={linea.descripcion}
          onChange={(e) => onChange({ descripcion: e.target.value })}
          className="flex-1 rounded-sm bg-bg-card border-[0.5px] border-white/15 text-text-primary px-2 py-1 text-sm placeholder:text-text-muted"
        />
        <input
          type="number"
          aria-label={`Monto línea ${indice + 1}`}
          min={0}
          step={1}
          value={Number.isFinite(linea.monto_aplicado) ? linea.monto_aplicado : 0}
          onChange={(e) =>
            onChange({ monto_aplicado: parseInt(e.target.value || '0', 10) })
          }
          className="w-28 rounded-sm bg-bg-card border-[0.5px] border-white/15 text-text-primary px-2 py-1 text-sm text-right"
        />
        {onEliminar && (
          <button
            type="button"
            onClick={onEliminar}
            aria-label={`Eliminar línea ${indice + 1}`}
            className="text-text-secondary hover:text-state-danger px-2 py-1 text-sm"
          >
            ✕
          </button>
        )}
      </div>

      {requiereCuota && (
        <select
          aria-label={`Cuota línea ${indice + 1}`}
          value={linea.cuota_id ?? ''}
          onChange={(e) => onChange({ cuota_id: e.target.value || null })}
          required
          className="w-full rounded-sm bg-bg-card border-[0.5px] border-white/15 text-text-primary px-2 py-1 text-sm"
        >
          <option value="">— Selecciona la cuota —</option>
          {cuotas.map((c) => (
            <option key={c.id} value={c.id}>
              {c.periodo} ({formatCLP(c.monto_total)}) · vence{' '}
              {formatFecha(c.vence_el)}
              {c.estado === 'en_abono' ? ' · en abono' : ''}
              {c.estado === 'vencida' ? ' · vencida' : ''}
            </option>
          ))}
        </select>
      )}

      {requiereRecurso && (
        <select
          aria-label={`Venta a cobrar línea ${indice + 1}`}
          value={linea.ingreso_vario_id ?? ''}
          onChange={(e) => {
            const iv = ventasPendientes.find((v) => v.id === e.target.value);
            onChange({
              ingreso_vario_id: e.target.value || null,
              recurso_id: null,
              // Al elegir la venta, precargar su monto y descripción.
              ...(iv
                ? { monto_aplicado: iv.monto, descripcion: iv.descripcion }
                : {}),
            });
          }}
          required
          className="w-full rounded-sm bg-bg-card border-[0.5px] border-white/15 text-text-primary px-2 py-1 text-sm"
        >
          <option value="">— Selecciona la venta a cobrar —</option>
          {ventasPendientes.map((v) => (
            <option key={v.id} value={v.id}>
              {v.descripcion} · {formatCLP(v.monto)}
            </option>
          ))}
        </select>
      )}
      {requiereRecurso && ventasPendientes.length === 0 && (
        <div className="bg-state-warning/10 border-l-4 border-state-warning p-2 rounded-sm text-xs text-text-secondary space-y-1">
          <p>No hay ventas pendientes de cobro para esta casa.</p>
          <p>
            La venta la crea la solicitud del vecino (al aprobarla) o una{' '}
            <a
              href="/tesoreria/recursos?tab=stock"
              className="text-orange underline hover:text-orange/80"
            >
              Asignación Directa en el tab Stock de Recursos
            </a>
            . Eso crea el ingreso por cobrar que aparecerá acá.
          </p>
        </div>
      )}

      {requiereCategoria && (
        <CategoriaSelector
          id={`linea-${indice}-categoria`}
          categorias={categoriasIngreso}
          value={linea.categoria_id ?? ''}
          onChange={(id) => onChange({ categoria_id: id || null })}
          required
          placeholder="— Selecciona la categoría —"
          className="w-full rounded-sm bg-bg-card border-[0.5px] border-white/15 text-text-primary px-2 py-1 text-sm"
        />
      )}
    </div>
  );
}
